import { useEffect } from 'react'
import { _doRefresh, endSession } from '@services/api'
import { useAuthStore } from '@store/authStore'
import { needsRefresh } from '@utils/session'

const CHECK_MS = 60_000

/**
 * Keeps a signed-in person signed in for as long as they are using the app. The access token is renewed a few minutes
 * before it runs out (checked every minute, and the moment the tab is shown again after being hidden), so a request
 * never fails for an expired token. Only when the renewal itself is refused (the refresh token is gone or was revoked,
 * for example after a password change) does the person see the sign-in page, and it brings them back here afterwards.
 */
export function useSessionKeeper() {
  const signedIn = useAuthStore((s) => s.isAuthenticated)
  useEffect(() => {
    if (!signedIn) return
    let busy = false
    const check = async () => {
      const { accessToken, refreshToken } = useAuthStore.getState()
      if (busy || !refreshToken || !needsRefresh(accessToken)) return
      busy = true
      try { await _doRefresh() } catch (e) {
        // Only a refusal ends the session. No connection is not a refusal: try again next time.
        const status = (e as { response?: { status?: number } }).response?.status
        if (status === 401 || status === 400) endSession()
      } finally { busy = false }
    }
    check()
    const id = setInterval(check, CHECK_MS)
    const onShow = () => { if (document.visibilityState === 'visible') check() }
    document.addEventListener('visibilitychange', onShow)
    window.addEventListener('online', check)
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', onShow); window.removeEventListener('online', check) }
  }, [signedIn])
}
